import React, { useState, useEffect, useRef } from 'react';
import {hooks} from '../../hooks';
import {text} from '../../text';
import {utils} from '../../utils';
import {svg} from '../../assets/svg';
import {theme} from '../../constants';
import {components} from '../../components';
import {RootState} from '../../store';
import axiosInstance from '../../services/axiosInstance';
import { useLanguage } from '../../context/LanguageContext';
import { getProfileCache, setProfileCache, patchProfileCache } from '../../utils/profileCache';

// Icons for Header
import { MdEmail, MdBadge, MdCalendarToday, MdPhone, MdWork, MdPerson, MdPostAdd, MdAccessTime, MdEventNote, MdReceipt, MdChevronRight, MdChevronLeft, MdAccountBalanceWallet, MdMail, MdClose } from 'react-icons/md';
const EmailIcon = MdEmail as any;
const BadgeIcon = MdBadge as any;
const CalendarIcon = MdCalendarToday as any;
const PhoneIcon = MdPhone as any;
const WorkIcon = MdWork as any;
const PersonIcon = MdPerson as any;
const PostAddIcon = MdPostAdd as any;
const AccessTimeIcon = MdAccessTime as any;
const EventNoteIcon = MdEventNote as any;
const ReceiptIcon = MdReceipt as any;
const ChevronRightIcon = MdChevronRight as any;
const ChevronLeftIcon = MdChevronLeft as any;
const WalletIcon = MdAccountBalanceWallet as any;
const MailIcon = MdMail as any;
const CloseIcon = MdClose as any;

export const PersonalData: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const auth = hooks.useAppSelector((state: RootState) => state.authSlice);
  const { t: translate, language } = useLanguage();
  const t = translate.personalData;

  const [employee, setEmployee] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  const nowDate = new Date();
  const [scoreMonth, setScoreMonth] = useState(`${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, '0')}`);
  const [scoreData, setScoreData] = useState<{ avg_score: number; task_count: number; prev_avg_score: number } | null>(null);
  const [scoreLoading, setScoreLoading] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showQrView, setShowQrView] = useState(false);
  const [originPos, setOriginPos] = useState({ tx: 100, ty: -100 });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const touchStartXRef = useRef<number>(0);
  const swipedRef = useRef<boolean>(false);

  // Crop state
  const CROP_SIZE = 260;
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState('');
  const [cropScale, setCropScale] = useState(1);
  const [cropFitScale, setCropFitScale] = useState(1);
  const [cropOffset, setCropOffset] = useState({ x: 0, y: 0 });
  const [cropNatural, setCropNatural] = useState({ w: 0, h: 0 });
  const cropImgRef = useRef<HTMLImageElement>(null);
  const cropDragRef = useRef<{ active: boolean; startX: number; startY: number; startOffX: number; startOffY: number; lastDist: number }>({
    active: false, startX: 0, startY: 0, startOffX: 0, startOffY: 0, lastDist: 0,
  });

  const imageBaseUrl = process.env.REACT_APP_IMAGE_BASE_URL || 'https://apiteratasks.teralintas.com/';

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  useEffect(() => {
    const fetchScore = async () => {
      setScoreLoading(true);
      try {
        const res = await axiosInstance.get(`/employees/my-avg-score?month=${scoreMonth}`);
        setScoreData(res.data);
      } catch (e) {
        console.error('Failed to fetch avg score', e);
      } finally {
        setScoreLoading(false);
      }
    };
    fetchScore();
  }, [scoreMonth]);

  const changeMonth = (delta: number) => {
    const [y, m] = scoreMonth.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setScoreMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const formatMonthLabel = (month: string) => {
    const [y, m] = month.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', { month: 'short', year: 'numeric' });
  };

  useEffect(() => {
    if (!showQrModal) {
      setTimeout(() => {
        setIsFlipped(false);
        setShowQrView(false);
      }, 400);
    }
  }, [showQrModal]);

  const fetchEmployeeData = async (forceRefresh = false) => {
    if (!forceRefresh) {
      const cached = getProfileCache(auth.userId!);
      if (cached) {
        setEmployee(cached);
        return;
      }
    }
    try {
      const response = await axiosInstance.get('/employees/profile');
      setEmployee(response.data);
      setProfileCache(auth.userId!, response.data);
    } catch (error) {
      console.error("Failed to fetch employee data", error);
      setEmployee({
        name: auth.userName,
        email: auth.email,
        leave_total: 12,
        leave_used: 0,
        employee_code: `EMP-${auth.userId}`,
        join_date: new Date().toISOString()
      });
    }
  };

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    // reset input agar onChange tetap trigger jika file sama dipilih lagi
    event.target.value = '';
    const reader = new FileReader();
    reader.onload = (e) => {
      setCropSrc(e.target?.result as string);
      setCropScale(1);
      setCropOffset({ x: 0, y: 0 });
      setCropOpen(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = async () => {
    const img = cropImgRef.current;
    if (!img) return;

    const canvas = document.createElement('canvas');
    canvas.width = CROP_SIZE;
    canvas.height = CROP_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clip circle
    ctx.beginPath();
    ctx.arc(CROP_SIZE / 2, CROP_SIZE / 2, CROP_SIZE / 2, 0, Math.PI * 2);
    ctx.clip();

    // Natural image size
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    // Scaled size di layar
    const displayW = nw * cropScale;
    const displayH = nh * cropScale;
    // Posisi gambar (tengah + offset)
    const drawX = CROP_SIZE / 2 - displayW / 2 + cropOffset.x;
    const drawY = CROP_SIZE / 2 - displayH / 2 + cropOffset.y;

    ctx.drawImage(img, drawX, drawY, displayW, displayH);

    canvas.toBlob(async (blob) => {
      if (!blob) return;
      setCropOpen(false);
      const formData = new FormData();
      formData.append('photo', blob, 'profile.jpg');
      try {
        setIsUploading(true);
        const response = await axiosInstance.post('/employees/upload-photo', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setEmployee((prev: any) => ({ ...prev, photo: response.data.photo }));
        patchProfileCache(auth.userId!, { photo: response.data.photo });
      } catch (error) {
        console.error('Failed to upload photo', error);
        alert('Gagal mengupload foto');
      } finally {
        setIsUploading(false);
      }
    }, 'image/jpeg', 0.92);
  };

  // Clamp offset so image always fully covers the circle
  const clampOffset = (ox: number, oy: number, scale: number, nw: number, nh: number) => {
    const hw = Math.max(0, nw * scale / 2 - CROP_SIZE / 2);
    const hh = Math.max(0, nh * scale / 2 - CROP_SIZE / 2);
    return {
      x: Math.min(hw, Math.max(-hw, ox)),
      y: Math.min(hh, Math.max(-hh, oy)),
    };
  };

  const applyScale = (next: number) => {
    const s = Math.min(5, Math.max(cropFitScale, next));
    setCropScale(s);
    setCropOffset(prev => clampOffset(prev.x, prev.y, s, cropNatural.w, cropNatural.h));
  };

  // Mouse drag handlers
  const onCropMouseDown = (e: React.MouseEvent) => {
    cropDragRef.current = { ...cropDragRef.current, active: true, startX: e.clientX, startY: e.clientY, startOffX: cropOffset.x, startOffY: cropOffset.y };
  };
  const onCropMouseMove = (e: React.MouseEvent) => {
    if (!cropDragRef.current.active) return;
    const raw = {
      x: cropDragRef.current.startOffX + (e.clientX - cropDragRef.current.startX),
      y: cropDragRef.current.startOffY + (e.clientY - cropDragRef.current.startY),
    };
    setCropOffset(clampOffset(raw.x, raw.y, cropScale, cropNatural.w, cropNatural.h));
  };
  const onCropMouseUp = () => { cropDragRef.current.active = false; };

  // Touch drag + pinch-zoom handlers
  const onCropTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      cropDragRef.current = { ...cropDragRef.current, active: true, startX: e.touches[0].clientX, startY: e.touches[0].clientY, startOffX: cropOffset.x, startOffY: cropOffset.y };
    } else if (e.touches.length === 2) {
      cropDragRef.current.active = false;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      cropDragRef.current.lastDist = Math.sqrt(dx * dx + dy * dy);
    }
  };
  const onCropTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1 && cropDragRef.current.active) {
      const raw = {
        x: cropDragRef.current.startOffX + (e.touches[0].clientX - cropDragRef.current.startX),
        y: cropDragRef.current.startOffY + (e.touches[0].clientY - cropDragRef.current.startY),
      };
      setCropOffset(clampOffset(raw.x, raw.y, cropScale, cropNatural.w, cropNatural.h));
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const delta = dist / (cropDragRef.current.lastDist || dist);
      cropDragRef.current.lastDist = dist;
      applyScale(cropScale * delta);
    }
  };
  const onCropTouchEnd = () => { cropDragRef.current.active = false; };

  // Scroll to zoom
  const onCropWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    applyScale(cropScale * (e.deltaY < 0 ? 1.1 : 0.9));
  };

  const renderCropModal = () => {
    if (!cropOpen) return null;
    return (
      <div style={{
        position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.85)',
        zIndex: 4000, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}>
        <text.H5 style={{ color: '#fff', marginBottom: 16, fontSize: 16 }}>Sesuaikan Foto Profil</text.H5>
        <text.T12 style={{ color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>Drag untuk menggeser · Scroll/Pinch untuk zoom</text.T12>

        {/* Crop area */}
        <div
          style={{
            width: CROP_SIZE, height: CROP_SIZE, borderRadius: CROP_SIZE / 2,
            overflow: 'hidden', border: '3px solid #fff',
            cursor: 'grab', position: 'relative', userSelect: 'none',
            backgroundColor: '#000',
          }}
          onMouseDown={onCropMouseDown}
          onMouseMove={onCropMouseMove}
          onMouseUp={onCropMouseUp}
          onMouseLeave={onCropMouseUp}
          onTouchStart={onCropTouchStart}
          onTouchMove={onCropTouchMove}
          onTouchEnd={onCropTouchEnd}
          onWheel={onCropWheel}
        >
          <img
            ref={cropImgRef}
            src={cropSrc}
            alt="crop"
            draggable={false}
            style={{
              position: 'absolute',
              width: cropNatural.w * cropScale,
              height: cropNatural.h * cropScale,
              left: `calc(50% + ${cropOffset.x}px)`,
              top: `calc(50% + ${cropOffset.y}px)`,
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }}
            onLoad={() => {
              const img = cropImgRef.current;
              if (!img) return;
              const nw = img.naturalWidth;
              const nh = img.naturalHeight;
              const fit = CROP_SIZE / Math.min(nw, nh);
              setCropNatural({ w: nw, h: nh });
              setCropFitScale(fit);
              setCropScale(fit);
              setCropOffset({ x: 0, y: 0 });
            }}
          />
        </div>

        {/* Zoom controls */}
        <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            onClick={() => applyScale(cropScale - 0.1)}
            style={{
              width: 34, height: 34, borderRadius: 17, border: '1.5px solid rgba(255,255,255,0.4)',
              backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 20, lineHeight: 1,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >−</button>
          <input
            type="range"
            min={cropFitScale}
            max={cropFitScale * 4}
            step={0.01}
            value={cropScale}
            onChange={e => applyScale(parseFloat(e.target.value))}
            style={{ width: 140, accentColor: theme.colors.mainColor }}
          />
          <button
            onClick={() => applyScale(cropScale + 0.1)}
            style={{
              width: 34, height: 34, borderRadius: 17, border: '1.5px solid rgba(255,255,255,0.4)',
              backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 20, lineHeight: 1,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >+</button>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={() => setCropOpen(false)}
            style={{
              padding: '12px 28px', borderRadius: 12, border: '1.5px solid rgba(255,255,255,0.3)',
              backgroundColor: 'transparent', color: '#fff', fontWeight: '600', fontSize: 15, cursor: 'pointer',
            }}
          >
            Batal
          </button>
          <button
            onClick={handleCropConfirm}
            style={{
              padding: '12px 28px', borderRadius: 12, border: 'none',
              backgroundColor: theme.colors.mainColor, color: '#fff', fontWeight: '700', fontSize: 15, cursor: 'pointer',
            }}
          >
            Simpan Foto
          </button>
        </div>
      </div>
    );
  };

  const leaveData = {
    total: employee?.leave_total || 12,
    used: employee?.leave_used || 0,
    remaining: employee?.leave_remaining ?? 0,
  };

  const renderQuickActions = () => {
    const actions = [
      { id: 'attendance', label: t.attendance || 'Kehadiran', icon: <AccessTimeIcon size={24} color="#3B82F6" />, bg: '#EFF6FF', route: '/Attendance' },
      { id: 'leave', label: t.leave || 'Cuti', icon: <EventNoteIcon size={24} color="#10B981" />, bg: '#ECFDF5', route: '/LeaveHistory' },
      { id: 'payslip', label: t.payslip || 'Slip Gaji', icon: <ReceiptIcon size={24} color="#8B5CF6" />, bg: '#F5F3FF', route: '/Payslip' },
      // { id: 'inbox', label: t.inbox || 'Kotak Masuk', icon: <MailIcon size={24} color="#F59E0B" />, bg: '#FEF3C7', route: '/Inbox' },
      { id: 'documents', label: (t as any).documents || 'Dokumen Saya', icon: <WalletIcon size={24} color="#F59E0B" />, bg: '#FEF3C7', route: '/DocumentHistory' },
    ];

    return (
      <div style={{ marginBottom: 24 }}>
        {/* <text.H5 style={{ marginBottom: 12, color: theme.colors.mainDark }}>{t.hrMenu || 'Menu Kepegawaian'}</text.H5> */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {actions.map(action => (
            <div
              key={action.id}
              onClick={() => navigate(action.route)}
              style={{
                backgroundColor: theme.colors.white,
                borderRadius: 16,
                padding: '12px 4px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                boxShadow: '0 4px 15px rgba(0,0,0,0.02)',
                border: '1px solid #F3F4F6',
                cursor: 'pointer',
              }}
            >
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                backgroundColor: action.bg,
                display: 'flex', justifyContent: 'center', alignItems: 'center',
                marginBottom: 8
              }}>
                {action.icon}
              </div>
              <text.T14 style={{ fontWeight: '600', color: theme.colors.mainDark, textAlign: 'center', fontSize: 11, whiteSpace: 'nowrap' }}>
                {action.label}
              </text.T14>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderHeader = () => {
    return (
      <components.Header
        title={t.title}
        user={true}
        plus={true}
        onPlus={() => setShowModal(true)}
      />
    );
  };

  const getProgressColor = (percent: number) => {
    if (percent > 80) return '#EF4444'; // Red
    if (percent > 50) return '#F59E0B'; // Orange
    return theme.colors.mainColor; // Default / Greenish
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
  };

  const renderProfileCard = () => {
    return (
      <div
        style={{
          marginBottom: 24,
          boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
          position: 'relative',
          borderRadius: 20,
        }}
      >
        {/* Background layer dengan overflow hidden agar lingkaran dekorasi terpotong rapi */}
        <div style={{
          position: 'absolute', inset: 0,
          background: `linear-gradient(135deg, ${theme.colors.mainColor} 0%, #4a90e2 100%)`,
          borderRadius: 20, overflow: 'hidden', zIndex: 0
        }}>
          {/* Decorative Circle */}
          <div style={{
              position: 'absolute', top: -20, right: -20, width: 100, height: 100, 
              borderRadius: 50, background: 'rgba(255,255,255,0.1)'
          }} />
        </div>

    {/* Area Interaktif Sudut Kanan Atas Lipatan Kertas */}
    <div
          style={{
        position: 'absolute', top: 0, right: 0, width: 65, height: 65,
            cursor: 'pointer',
            zIndex: 2,
          }}
      onClick={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const tx = centerX - window.innerWidth / 2;
        const ty = centerY - window.innerHeight / 2;
        setOriginPos({ tx, ty });
        setTimeout(() => setShowQrModal(true), 10);
      }}
    >
      {/* Latar belakang putih & Masking untuk efek potongan (cutout) di bawah lipatan */}
      <div 
        style={{
          position: 'absolute', inset: 0,
          backgroundColor: '#FFFFFF',
          borderTopRightRadius: 20,
          overflow: 'hidden',
          WebkitMaskImage: 'linear-gradient(225deg, black 50.5%, transparent 50.5%)',
          maskImage: 'linear-gradient(225deg, black 50.5%, transparent 50.5%)',
        }}
      >
        {/* Efek Mengintip - Dibuat seukuran profile card */}
        <img 
          src="/images/qr_example.png" 
          alt="QR Peek" 
          style={{ 
            position: 'absolute', top: -15, right: -15,
            width: 130, height: 130, opacity: 0.25, mixBlendMode: 'multiply',
            maxWidth: 'none'
          }} 
        />
      </div>

      {/* Efek kertas yang tergulung/melengkung (curled paper) */}
      <div 
        style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(225deg, transparent 49.5%, rgba(255,255,255,0.95) 49.5%, #d1d5db 100%)',
          borderBottomLeftRadius: 30,
          boxShadow: '-4px 4px 10px rgba(0,0,0,0.15)',
        }}
      />
    </div>

        {/* Content Container */}
        <div style={{
          padding: 24, position: 'relative', zIndex: 1, color: theme.colors.white,
          display: 'flex', alignItems: 'center', gap: 16, width: '100%', boxSizing: 'border-box'
        }}>
          <div
            onClick={handlePhotoClick}
            style={{
              width: 70,
              height: 70,
              minWidth: 70,
              minHeight: 70,
              borderRadius: 35,
              backgroundColor: theme.colors.white,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              border: '3px solid rgba(255,255,255,0.3)',
              cursor: 'pointer',
              overflow: 'hidden',
              position: 'relative',
              flexShrink: 0,
              boxSizing: 'border-box'
            }}
          >
            {employee?.photo ? (
               <img src={`${imageBaseUrl}${employee.photo}`} alt="profile" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
            ) : (
               <PersonIcon size={35} color={theme.colors.mainColor} />
            )}
            {isUploading && (
                <div style={{position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                    <div className="spinner" style={{width: 20, height: 20, borderRadius: 10, border: '2px solid white', borderTopColor: 'transparent', animation: 'spin 1s linear infinite'}} />
                </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
            <text.H3 style={{color: theme.colors.white, marginBottom: 4, wordBreak: 'break-word'}}>
              {employee?.name || auth.userName}
            </text.H3>
            <text.T14 style={{color: 'rgba(255,255,255,0.9)', textTransform: 'capitalize', marginBottom: 4, wordBreak: 'break-word'}}>
              {employee?.department_name || t.employeeLabel || 'Employee'}
            </text.T14>
            <text.T12 style={{color: 'rgba(255,255,255,0.7)', wordBreak: 'break-word'}}>{t.uploadPhoto}</text.T12>
          </div>
        </div>

        {/* QR Modal Overlay */}
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: '#000',
            opacity: showQrModal ? 1 : 0, transition: 'opacity 0.4s ease',
            zIndex: 2999, pointerEvents: showQrModal ? 'auto' : 'none',
          }}
          onClick={() => setShowQrModal(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setShowQrModal(false); }}
            style={{
              position: 'absolute', top: 16, right: 16,
              width: 36, height: 36, borderRadius: 18,
              backgroundColor: 'rgba(255,255,255,0.15)',
              border: '1px solid rgba(255,255,255,0.3)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <CloseIcon size={20} color="#fff" />
          </button>
        </div>

        {/* QR Modal Content - Card Flip Animation */}
        <div
          style={{
            position: 'fixed', top: '50%', left: '50%',
            width: '90vw', maxWidth: 380,
            perspective: 1000,
            transformOrigin: 'center',
            transform: showQrModal ? 'translate(-50%, -50%) scale(1)' : `translate(calc(-50% + ${originPos.tx}px), calc(-50% + ${originPos.ty}px)) scale(0)`,
            opacity: showQrModal ? 1 : 0,
            transition: 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.4s ease',
            zIndex: 3000, pointerEvents: showQrModal ? 'auto' : 'none',
          }}
        >
          <div
            onClick={(e) => { e.stopPropagation(); if (!swipedRef.current) setIsFlipped(prev => !prev); }}
            onTouchStart={(e) => { touchStartXRef.current = e.touches[0].clientX; swipedRef.current = false; }}
            onTouchEnd={(e) => {
              const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
              if (Math.abs(deltaX) > 50) { swipedRef.current = true; setIsFlipped(prev => !prev); }
            }}
            style={{
              width: '100%', height: '100%',
              position: 'relative',
              transition: 'transform 0.6s',
              transformStyle: 'preserve-3d',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
              cursor: 'pointer',
            }}
          >
            {/* Front: Business Card Image / QR Code */}
            <div
              style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                backgroundColor: 'white', borderRadius: 16,
                display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                backfaceVisibility: 'hidden',
                color: theme.colors.mainDark,
                boxSizing: 'border-box'
              }}
            >
               <text.H4 style={{ marginBottom: 16, color: theme.colors.mainDark }}>
                 {showQrView ? (t.qrCode || 'QR Code') : (t.businessCard || 'Business Card')}
               </text.H4>
               <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                 {showQrView ? (
                   employee?.business_card_url ? (
                     <img src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(`${imageBaseUrl}${employee.business_card_url.replace(/\\/g, '/')}`)}`} alt="Business Card QR Code" style={{ width: '100%', maxWidth: 250, height: 'auto' }} />
                   ) : (
                     <div style={{ width: 220, height: 220, backgroundColor: '#F3F4F6', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 10 }}>
                       <text.T14 style={{color: '#9CA3AF'}}>No Business Card</text.T14>
                     </div>
                   )
                 ) : (
                   employee?.business_card_url ? (
                     <img src={`${imageBaseUrl}${employee.business_card_url.replace(/\\/g, '/')}`} alt="Business Card" style={{ width: '100%', height: 'auto', maxHeight: 200, objectFit: 'contain', borderRadius: 8 }} />
                   ) : (
                     <div style={{ width: 220, height: 160, backgroundColor: '#F3F4F6', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: 10 }}>
                       <text.T14 style={{color: '#9CA3AF'}}>No Business Card</text.T14>
                     </div>
                   )
                 )}
               </div>
               {/* Show QR / Show Card toggle button */}
               <button
                 onClick={(e) => { e.stopPropagation(); setShowQrView(prev => !prev); }}
                 style={{
                   marginTop: 16,
                   padding: '8px 22px',
                   borderRadius: 20,
                   border: `1.5px solid ${theme.colors.mainColor}`,
                   backgroundColor: showQrView ? theme.colors.mainColor : 'transparent',
                   color: showQrView ? '#fff' : theme.colors.mainColor,
                   fontSize: 13,
                   fontWeight: '600',
                   cursor: 'pointer',
                   fontFamily: 'inherit',
                 }}
               >
                 {showQrView ? (t.showCard || 'Lihat Kartu') : (t.showQr || 'Tampilkan QR')}
               </button>
               <text.T14 style={{ marginTop: 10, color: '#9CA3AF', fontWeight: '500' }}>{t.tapToSeeIdCard || 'Ketuk untuk melihat Kartu ID ↺'}</text.T14>
            </div>

            {/* Back: ID Card */}
            <div
              style={{
                position: 'relative', width: '100%', minHeight: 500, maxHeight: '85vh',
                backgroundColor: 'white', borderRadius: 16,
                display: 'flex', flexDirection: 'column',
                boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                color: theme.colors.mainDark,
                overflow: 'hidden',
                boxSizing: 'border-box'
              }}
            >
              {employee?.id_card_url ? (
                <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#F3F4F6' }}>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', position: 'relative' }}>
                    {employee.id_card_url.toLowerCase().endsWith('.pdf') ? (
                        <object data={`${imageBaseUrl}${employee.id_card_url.replace(/\\/g, '/')}#toolbar=0&navpanes=0&scrollbar=0`} type="application/pdf" width="100%" height="500px" style={{ objectFit: 'contain', margin: 'auto 0' }}>
                           <text.T14 style={{textAlign: 'center', marginTop: 20}}>PDF Preview</text.T14>
                        </object>
                    ) : (
                        <img src={`${imageBaseUrl}${employee.id_card_url.replace(/\\/g, '/')}`} alt="ID Card" style={{ width: '100%', height: 'auto', display: 'block', margin: 'auto 0' }} />
                    )}
                  </div>
                </div>
              ) : (
                <>
               {/* ID Card Header */}
               <div style={{ backgroundColor: theme.colors.mainColor, padding: '20px 16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                 <text.H4 style={{ color: 'white', margin: 0, letterSpacing: 2 }}>{t.idCard || 'ID CARD'}</text.H4>
               </div>
               
               <div style={{ padding: '24px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                 {/* Photo */}
                 <div style={{ width: 100, height: 100, borderRadius: 50, border: `3px solid ${theme.colors.mainColor}`, overflow: 'hidden', marginBottom: 20 }}>
                   {employee?.photo ? (
                     <img src={`${imageBaseUrl}${employee.photo}`} alt="profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                   ) : (
                     <div style={{width: '100%', height: '100%', backgroundColor: '#F3F4F6', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                        <PersonIcon size={55} color="#9CA3AF" />
                     </div>
                   )}
                 </div>
                 
                 {/* Details */}
                 <text.H4 style={{ textAlign: 'center', marginBottom: 4, textTransform: 'capitalize' }}>{employee?.name || auth.userName}</text.H4>
                 <text.T14 style={{ color: theme.colors.mainColor, fontWeight: 'bold', marginBottom: 24, textTransform: 'uppercase', letterSpacing: 1 }}>{employee?.department_name || t.employeeLabel || 'Employee'}</text.T14>
                 
                 <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 12, flex: 1 }}>
                   <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F3F4F6', paddingBottom: 6 }}>
                     <text.T12 style={{ color: '#9CA3AF' }}>{t.idNumber || 'ID Number'}</text.T12>
                     <text.T14 style={{ fontWeight: '600' }}>{employee?.employee_code || `EMP-${auth.userId}`}</text.T14>
                   </div>
                   <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F3F4F6', paddingBottom: 6 }}>
                     <text.T12 style={{ color: '#9CA3AF' }}>Email</text.T12>
                     <text.T14 style={{ fontWeight: '600' }}>{employee?.user_email || auth.email || '-'}</text.T14>
                   </div>
                   <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F3F4F6', paddingBottom: 6 }}>
                     <text.T12 style={{ color: '#9CA3AF' }}>Phone</text.T12>
                     <text.T14 style={{ fontWeight: '600' }}>{employee?.user_phone || '-'}</text.T14>
                   </div>
                 </div>
               </div>
               </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderLeaveStats = () => {
    const percentage = (leaveData.used / leaveData.total) * 100;
    const progressColor = getProgressColor(percentage);

    const getScoreColor = (score: number) => {
      if (score >= 90) return '#10B981';
      if (score >= 50) return '#F59E0B';
      return '#EF4444';
    };

    const currentMonthStr = `${nowDate.getFullYear()}-${String(nowDate.getMonth() + 1).padStart(2, '0')}`;
    const isCurrentMonth = scoreMonth >= currentMonthStr;
    const avgScore = scoreData?.avg_score ?? 0;
    const delta = (scoreData && scoreData.prev_avg_score > 0)
      ? Math.round((scoreData.avg_score - scoreData.prev_avg_score) * 10) / 10
      : null;

    return (
      <div style={{marginBottom: 24, display: 'flex', gap: 16}}>
        {/* Leave Balance Card */}
        <div style={{
          flex: 1, backgroundColor: theme.colors.white, borderRadius: 16, padding: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #F4F6F8',
          display: 'flex', flexDirection: 'column', justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <text.T12 style={{color: '#9CA3AF', marginBottom: 4, fontWeight: '500'}}>{t.leaveBalance}</text.T12>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                <text.H2 style={{color: theme.colors.mainDark, lineHeight: 1}}>{leaveData.remaining}</text.H2>
                <text.T14 style={{color: '#6B7280', fontWeight: '600', paddingBottom: 2}}>{t.days}</text.T14>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <text.T12 style={{color: '#6B7280', fontWeight: '500'}}>
                <span style={{color: theme.colors.mainDark, fontWeight: 'bold'}}>{leaveData.used}</span> / {leaveData.total}
              </text.T12>
            </div>
          </div>
          <div style={{height: 8, width: '100%', backgroundColor: '#F3F4F6', borderRadius: 4, overflow: 'hidden'}}>
            <div style={{
              height: '100%', width: `${Math.min(percentage, 100)}%`,
              backgroundColor: progressColor, borderRadius: 4, transition: 'width 0.5s ease'
            }} />
          </div>
        </div>

        {/* Average Score Card */}
        <div style={{
          flex: 1, backgroundColor: theme.colors.white, borderRadius: 16, padding: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #F4F6F8',
          display: 'flex', flexDirection: 'column', gap: 6
        }}>
          {/* Title + Month navigator */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <text.T12 style={{color: '#9CA3AF', fontWeight: '500'}}>{t.averageScore}</text.T12>
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <div onClick={() => changeMonth(-1)} style={{ cursor: 'pointer', padding: '2px 0px', lineHeight: 1 }}>
                <ChevronLeftIcon size={14} color="#9CA3AF" />
              </div>
              <text.T12 style={{ color: '#6B7280', fontWeight: '600', fontSize: 10, whiteSpace: 'nowrap' }}>
                {formatMonthLabel(scoreMonth)}
              </text.T12>
              <div
                onClick={() => { if (!isCurrentMonth) changeMonth(1); }}
                style={{ cursor: isCurrentMonth ? 'default' : 'pointer', padding: '2px 0px', lineHeight: 1, opacity: isCurrentMonth ? 0.25 : 1 }}
              >
                <ChevronRightIcon size={14} color="#9CA3AF" />
              </div>
            </div>
          </div>

          {/* Score value */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
            <text.H2 style={{ color: scoreLoading ? '#D1D5DB' : theme.colors.mainDark, lineHeight: 1 }}>
              {scoreLoading ? '—' : avgScore}
            </text.H2>
            <text.T14 style={{color: '#6B7280', fontWeight: '600', paddingBottom: 2}}>{t.pts}</text.T14>
          </div>

          {/* Progress bar */}
          <div style={{height: 8, width: '100%', backgroundColor: '#F3F4F6', borderRadius: 4, overflow: 'hidden'}}>
            <div style={{
              height: '100%', width: `${Math.min(avgScore, 100)}%`,
              backgroundColor: getScoreColor(avgScore), borderRadius: 4, transition: 'width 0.5s ease'
            }} />
          </div>

          {/* Delta + task count */}
          {!scoreLoading && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {(scoreData?.task_count ?? 0) > 0 && delta !== null ? (
                <text.T12 style={{ color: delta >= 0 ? '#10B981' : '#EF4444', fontWeight: '600', fontSize: 10 }}>
                  {delta > 0 ? '+' : ''}{delta} {(t as any).vsLastMonth}
                </text.T12>
              ) : (
                <text.T12 style={{ color: '#9CA3AF', fontSize: 10 }}>{(t as any).noScoreData}</text.T12>
              )}
              {(scoreData?.task_count ?? 0) > 0 && (
                <text.T12 style={{ color: '#9CA3AF', fontSize: 10 }}>
                  {scoreData!.task_count} {(t as any).tasks}
                </text.T12>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDetailItem = (icon: any, label: string, value: string) => (
    <div style={{
        display: 'flex', 
        alignItems: 'center', 
        padding: '10px 0', 
        borderBottom: '1px solid #F3F4F6'
    }}>
        <div style={{
            width: 36, height: 36, borderRadius: 10, 
            backgroundColor: '#F3F4F6', display: 'flex', 
            justifyContent: 'center', alignItems: 'center', marginRight: 12
        }}>
            {icon}
        </div>
        <div style={{flex: 1}}>
            <text.T12 style={{color: '#9CA3AF', marginBottom: 2}}>{label}</text.T12>
            <text.T16 style={{color: theme.colors.mainDark, fontWeight: '500'}}>{value}</text.T16>
        </div>
    </div>
  );

  const renderDetails = () => {
    return (
      <div style={{marginBottom: 24}}>
        <text.H5 style={{marginBottom: 10, color: theme.colors.mainDark}}>{t.personalInfo}</text.H5>
        <div style={{
            backgroundColor: theme.colors.white,
            borderRadius: 16,
            padding: '4px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            border: '1px solid #F4F6F8',
        }}>
            {renderDetailItem(<EmailIcon size={20} color={theme.colors.mainColor} />, t.email, employee?.user_email || auth.email || '-')}
            {renderDetailItem(<BadgeIcon size={20} color={theme.colors.mainColor} />, t.employeeId, employee?.employee_code || `EMP-${auth.userId}`)}
            {renderDetailItem(<CalendarIcon size={20} color={theme.colors.mainColor} />, t.joinDate, formatDate(employee?.join_date))}
            {renderDetailItem(<PhoneIcon size={20} color={theme.colors.mainColor} />, t.phone, employee?.user_phone || '-')}
            <div style={{borderBottom: 'none'}}>
             {renderDetailItem(<WorkIcon size={20} color={theme.colors.mainColor} />, t.department, employee?.department_name || '-')}
            </div>
        </div>
      </div>
    );
  };

  const renderModal = () => {
    if (!showModal) return null;
    
    return (
        <div 
            onClick={() => setShowModal(false)}
            style={{
                position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
                zIndex: 2000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20
            }}
        >
            <div 
                onClick={(e) => e.stopPropagation()}
                style={{
                    backgroundColor: 'white', width: '100%', maxWidth: 320, 
                    borderRadius: 16, padding: 24, boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}
            >
                <text.H4 style={{marginBottom: 20}}>{t.addDocument}</text.H4>
                
                <div
                    onClick={() => {
                        setShowModal(false);
                        navigate('/LeaveRequest');
                    }}
                    style={{
                        padding: '15px 0',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderBottom: '1px solid #F3F4F6'
                    }}
                >
                    <PostAddIcon size={22} color={theme.colors.mainDark} style={{marginRight: 15}} />
                    <text.T16 style={{color: theme.colors.mainDark}}>{t.leaveRequest}</text.T16>
                </div>

                <div
                    onClick={() => {
                        setShowModal(false);
                        navigate('/Attendance', { state: { openCorrection: true } });
                    }}
                    style={{
                        padding: '15px 0',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        borderBottom: '1px solid #F3F4F6'
                    }}
                >
                    <AccessTimeIcon size={22} color={theme.colors.mainDark} style={{marginRight: 15}} />
                    <text.T16 style={{color: theme.colors.mainDark}}>{(t as any).correctionRequest || 'Koreksi Absensi'}</text.T16>
                </div>

                <div
                    onClick={() => {
                        setShowModal(false);
                        navigate('/LoanRequest');
                    }}
                    style={{
                        padding: '15px 0',
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer'
                    }}
                >
                    <WalletIcon size={22} color={theme.colors.mainDark} style={{marginRight: 15}} />
                    <text.T16 style={{color: theme.colors.mainDark}}>{(t as any).loanRequest || 'Kasbon / Pinjaman'}</text.T16>
                </div>

                <components.Button 
                    title={t.cancel} 
                    colorScheme="light"
                    containerStyle={{marginTop: 20}}
                    onClick={() => setShowModal(false)}
                />
            </div>
        </div>
    );
  };

  return (
    <div id='screen'>
        {/* Hidden Input for File Upload */}
        <input 
            type="file" 
            ref={fileInputRef} 
            style={{display: 'none'}} 
            accept="image/*" 
            onChange={handleFileChange} 
        />

        {renderHeader()}
        <main
            className='container'
            style={{marginTop: 60, paddingBottom: 110, height: 'calc(100vh - 60px)', overflowY: 'auto'}}
        >
            {renderProfileCard()}
            {renderQuickActions()}
            {renderLeaveStats()}
            {renderDetails()}
        </main>
        <components.BottomTabBar />
        {renderModal()}
        {renderCropModal()}
    </div>
  );
};
